import * as React from "react";
import { useState } from "react";
import { Field, useField } from "formik";
import { Accordion, AccordionSummary, AccordionDetails, IconButton, InputAdornment } from "@material-ui/core";
import ListItemText from "@material-ui/core/ListItemText";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { green, yellow, red, grey } from "@material-ui/core/colors";
import { TextField } from "../Forms/fields/TextField";
import { CheckboxField } from "../Forms/fields/CheckboxField";
import { KeyboardArrowUp, KeyboardArrowDown } from "@material-ui/icons";
import NumberFormat from "react-number-format";
import { NumberFormatCustomProps } from "../Forms/fields/CurrencyInput";
import { always, cond, isNil, lte, prop, T, __ } from "ramda";
import { Select } from "../Forms/fields/Select";
import { PadThickness } from "../../generated/nest-graphql";
import { flow } from "fp-ts/lib/function";

function NumberFormatThickness(props: NumberFormatCustomProps) {
  const { inputRef, onChange, ...other } = props;
  const cleanValRef = React.useRef<any>();
  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        cleanValRef.current = values;
      }}
      onChange={(e) => onChange({ target: { name: props.name, value: cleanValRef.current?.value } })}
      decimalScale={0}
      isAllowed={(values) => {
        const { value } = values;
        return Number(value) < 100;
      }}
      allowNegative={false}
      isNumericString
    />
  );
}

const useStyles = makeStyles({
  accordionSummary: {
    border: "2px solid rgb(212, 212, 212)",
    borderRadius: 20,
    "& .MuiAccordionSummary-content": {
      margin: "0px",
    },
    "& .MuiListItemText-root": {
      display: "flex",
      alignItems: "center",
    },
    display: "flex",
    justifyContent: "center",
  },
  accordion: {
    borderRadius: 20,
    "& .MuiAccordionDetails-root": {
      padding: "0px",
    },
    marginBottom: "15px",
    "&.MuiAccordion-root:before": {
      display: "none",
    },
  },
  accordionDetails: {
    "& .MuiAccordionDetails-root": {
      padding: "0px",
    },
  },
  dateInput: {
    "& .MuiFormControl-root": {
      flexGrow: 1,
    },
  },
  checkboxLabelSize: {
    fontSize: 12,
  },
  checkboxLabelMargin: {
    marginRight: 0,
    "& span": {
      paddingBottom: "2px",
    },
  },
  goodCondition: {
    backgroundColor: green[500],
    "&:focus": {
      backgroundColor: green[500],
    },
  },
  okayCondition: {
    backgroundColor: yellow[500],
    "&:focus": {
      backgroundColor: yellow[500],
    },
  },
  badCondition: {
    backgroundColor: red[500],
    "&:focus": {
      backgroundColor: red[500],
    },
  },
  na: {
    backgroundColor: grey[500],
    "&:focus": {
      backgroundColor: grey[500],
    },
  },
});

const getColor = (classes, padThickness: PadThickness, condition: string) => {
  if (condition !== "Good condition") {
    return classes.badCondition;
  }

  return cond([
    [prop("unavailable"), always(classes.na)],
    [flow(prop("thickness"), lte(__, 3)), always(classes.badCondition)],
    [flow(prop("thickness"), lte(__, 5)), always(classes.okayCondition)],
    [T, always(classes.goodCondition)],
  ])(padThickness);
};

export const BrakePadsDropdownListItem: React.FC<{
  name: string;
  label: string;
}> = ({ name, label }) => {
  const [expanded, setExpanded] = useState(false);
  const classes = useStyles();
  const [field, meta, helper] = useField(name);
  const { padThickness, condition } = field.value;
  return (
    <Accordion className={`${classes.accordion}`} expanded={expanded}>
      <AccordionSummary
        className={`${classes.accordionSummary}  ${!meta.error && getColor(classes, padThickness, condition)}`}
        onClick={() => setExpanded(!expanded)}
      >
        <ListItemText primary={label} />
        <div>
          <IconButton>{expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconButton>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className="flex flex-col flex-grow">
          <div className="flex justify-between items-center m-2">
            <TextField
              name={`${name}.padThickness.thickness`}
              label="Pad Thickness"
              disabled={padThickness.unavailable}
              errorText=""
              inputMode="numeric"
              textAlign="right"
              InputProps={{
                inputComponent: NumberFormatThickness as any,
                endAdornment: <InputAdornment position="end">mm</InputAdornment>,
              }}
            />
            <CheckboxField
              name={`${name}.padThickness.unavailable`}
              label="Unavailable"
              labelPlacement="bottom"
              classNames="pb-2"
              labelClasses={{
                label: classes.checkboxLabelSize,
                labelPlacementBottom: classes.checkboxLabelMargin,
              }}
              postOnChange={(val) => {
                if (val) {
                  const resetField = { ...field.value };
                  resetField.padThickness.thickness = null;
                  resetField.padThickness.unavailable = val;
                  helper.setValue(resetField);
                }
              }}
            />
          </div>
          <div className="m-2">
            <Field name={`${name}.condition`}>
              {({ field }) => (
                <Select
                  {...field}
                  fullWidth={true}
                  options={["Good condition", "Cracked", "Grooved", "Uneven wear", "Metal to metal"]}
                  label={"Condition"}
                  errorMessage={isNil(condition) ? "No brake pad condition selected." : ""}
                />
              )}
            </Field>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
